import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Github, Loader2, CheckCircle2, ExternalLink, AlertTriangle, Lock, Unlock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Switch } from './ui/switch';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DeployGitHubModal({
  open,
  onOpenChange,
  sessionId,
  projectName,
  hasProject,
  isAuthed,
  onLogin,
}) {
  const [status, setStatus] = useState({ loading: true, connected: false, login: null });
  const [repoName, setRepoName] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [result, setResult] = useState(null);

  // Pull connection status whenever the modal opens
  useEffect(() => {
    if (!open || !isAuthed) {
      setResult(null);
      return;
    }
    let cancel = false;
    (async () => {
      setStatus({ loading: true, connected: false, login: null });
      try {
        const r = await axios.get(`${API}/auth/github/status`, { withCredentials: true });
        if (!cancel) {
          setStatus({ loading: false, connected: !!r.data?.connected, login: r.data?.login || null });
        }
      } catch (_) {
        if (!cancel) setStatus({ loading: false, connected: false, login: null });
      }
    })();
    return () => { cancel = true; };
  }, [open, isAuthed]);

  // Sensible default repo name based on the project
  useEffect(() => {
    if (projectName && !repoName) {
      const slug = projectName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      setRepoName(slug || 'transformerus-app');
    }
  }, [projectName, repoName]);

  const startConnect = () => {
    const origin = window.location.origin;
    window.location.href = `${API}/auth/github/connect?origin=${encodeURIComponent(origin)}`;
  };

  const startDeploy = async () => {
    setDeploying(true);
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/build/${sessionId}/deploy/github`,
        { repo_name: repoName.trim(), private: isPrivate },
        { withCredentials: true, timeout: 90000 },
      );
      setResult(r.data);
      toast.success('Pushed to GitHub!');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Deploy failed. Please try again.';
      toast.error(msg);
      setResult({ ok: false, error: msg });
    } finally {
      setDeploying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="deploy-github-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Github className="w-5 h-5" /> Deploy to GitHub
          </DialogTitle>
          <DialogDescription>
            Push this build to a new repository under your GitHub account in one click.
          </DialogDescription>
        </DialogHeader>

        {!isAuthed ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Sign in first to save and deploy your builds.</span>
            </div>
            <Button onClick={onLogin} className="w-full h-9 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="deploy-signin-btn">
              Sign in
            </Button>
          </div>
        ) : !hasProject ? (
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-600">
            Generate something first — then come back here to ship it.
          </div>
        ) : status.loading ? (
          <div className="flex items-center gap-2 text-sm text-neutral-500 py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking GitHub connection…
          </div>
        ) : !status.connected ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-700">
              <p>
                Connect your GitHub account so Transformerus can create a new repo and push your build for you.
              </p>
              <p className="mt-2 text-xs text-neutral-500">
                We'll request the <code className="font-mono">repo</code> scope so we can create and write to repositories on your behalf.
              </p>
            </div>
            <Button onClick={startConnect} className="w-full h-10 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="deploy-connect-github-btn">
              <Github className="w-4 h-4 mr-2" /> Connect GitHub
            </Button>
          </div>
        ) : result?.ok ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <div className="font-semibold">Pushed to GitHub.</div>
                  <div className="text-xs">{result.files_pushed} files pushed{result.errors?.length ? `, ${result.errors.length} errors` : ''}.</div>
                </div>
              </div>
            </div>
            <a
              href={result.repo_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full"
              data-testid="deploy-open-repo-link"
            >
              <Button className="w-full h-10 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-white">
                <ExternalLink className="w-4 h-4 mr-2" /> Open repository
              </Button>
            </a>
            <p className="text-xs text-neutral-500 text-center">
              Clone &amp; run locally: <code className="font-mono">git clone {result.repo_url}.git &amp;&amp; cd {result.repo_full_name?.split('/')[1] || 'app'} &amp;&amp; npm install &amp;&amp; npm run dev</code>
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-xs text-neutral-500 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
              Connected as <span className="font-medium text-neutral-700">@{status.login || 'github'}</span>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-neutral-700">Repository name</label>
              <Input
                value={repoName}
                onChange={(e) => setRepoName(e.target.value)}
                placeholder="my-awesome-app"
                data-testid="deploy-repo-name-input"
                className="h-9 text-sm"
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-neutral-200 bg-neutral-50 p-3">
              <div className="flex items-center gap-2 text-sm text-neutral-700">
                {isPrivate ? <Lock className="w-4 h-4 text-neutral-500" /> : <Unlock className="w-4 h-4 text-neutral-500" />}
                <span>{isPrivate ? 'Private repository' : 'Public repository'}</span>
              </div>
              <Switch checked={isPrivate} onCheckedChange={setIsPrivate} data-testid="deploy-private-switch" />
            </div>

            <Button
              onClick={startDeploy}
              disabled={deploying || !repoName.trim()}
              className="w-full h-10 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-white disabled:opacity-50"
              data-testid="deploy-push-btn"
            >
              {deploying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating repo &amp; pushing…</> : <><Github className="w-4 h-4 mr-2" /> Create repo &amp; push</>}
            </Button>
            {result?.error && (
              <div className="text-xs text-rose-600">{result.error}</div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
